import { fetchClient } from '../lib/openapi-query';
import type { UpdateProfileRequest, UpdateTimezoneRequest, UserProfile } from '../types/profile';

export class ProfileService {
  /**
   * Get current user profile
   */
  static async getProfile(): Promise<UserProfile> {
    const response = await fetchClient.GET('/profile');
      
    if (response.error) {
      throw new Error(response.error.message ?? 'Failed to fetch profile information');
    }
    
    return response.data as unknown as UserProfile;
  }

  /**
   * Update user profile
   */
  static async updateProfile(updates: UpdateProfileRequest): Promise<UserProfile> {
    // Validate that at least one field is provided
    if (Object.keys(updates).length === 0) {
      throw new Error('At least one field must be provided for update');
    }

    const response = await fetchClient.PATCH('/profile', {
      body: {
        name: updates.personalData?.firstName && updates.personalData?.lastName 
          ? `${updates.personalData.firstName} ${updates.personalData.lastName}`
          : undefined,
        phoneNumber: updates.personalData?.phoneNumber,
        dateOfBirth: updates.personalData?.dateOfBirth,
        countryCode: updates.personalData?.countryCode,
        phoneCode: updates.personalData?.phoneCode,
      }
    });
    
    if (response.error) {
      throw new Error('Failed to update profile information');
    }
    
    return response.data as unknown as UserProfile;
  }

  /**
   * Update user timezone
   */
  static async updateTimezone(timezone: string): Promise<UserProfile> {
    try {
      const request: UpdateTimezoneRequest = { timezone };
      const response = await fetchClient.PATCH('/profile/timezone', {
        body: request
      });
      
      if (response.error) {
        throw new Error('Failed to update timezone');
      }
      
      return response.data as unknown as UserProfile;
    } catch (error) {
      console.error('Failed to update timezone:', error);
      throw new Error('Failed to update timezone');
    }
  }

  /**
   * Delete user profile (soft delete)
   */
  static async deleteProfile(): Promise<{ message: string }> {
    const response = await fetchClient.DELETE('/profile');
    
    if (response.error) {
      throw new Error('Failed to delete profile');
    }
    
    return response.data as unknown as { message: string };
  }

  /**
   * Validate email format
   */
  static validateEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  /**
   * Validate phone number format
   */
  static validatePhoneNumber(phoneNumber: string): boolean {
    const phoneRegex = /^\+?[\d\s\-()]+$/;
    return phoneRegex.test(phoneNumber) && phoneNumber.replace(/\D/g, '').length >= 10;
  }

  /**
   * Validate date of birth format (YYYY-MM-DD)
   */
  static validateDateOfBirth(dateOfBirth: string): boolean {
    const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
    if (!dateRegex.test(dateOfBirth)) {
      return false;
    }
    
    const date = new Date(dateOfBirth);
    const now = new Date();
    
    // Check if date is valid and not in the future
    return !isNaN(date.getTime()) && date < now;
  }

  /**
   * Format phone number for display
   */
  static formatPhoneNumber(countryCode: string, phoneCode: string, phoneNumber: string): string {
    return `${countryCode} ${phoneCode} ${phoneNumber}`;
  }

  /**
   * Format time for display
   */
  static formatTime(hour: number, minute: number): string {
    const h = hour.toString().padStart(2, '0');
    const m = minute.toString().padStart(2, '0');
    return `${h}:${m}`;
  }

  /**
   * Parse time string to hour and minute
   */
  static parseTime(timeStr: string): { hour: number; minute: number } {
    const [hour, minute] = timeStr.split(':').map(Number);
    return { hour, minute };
  }
}